/**
 * Utility functions for date and time formatting
 */

/**
 * Format a date string to localized date and time
 * Handles ISO 8601 strings, timestamps, and various date formats
 */
export function formatDateTime(dateString: string | number | Date) {
  try {
    // Handle null/undefined
    if (!dateString) {
      return { date: 'Не указано', time: '' }
    }

    // Parse the date - handle ISO strings, timestamps, Date objects
    let date: Date

    if (typeof dateString === 'string') {
      // ISO 8601 format with timezone (e.g., "2024-03-15T10:30:00.000Z" or "2024-03-15T10:30:00+06:00")
      date = new Date(dateString)
    } else if (typeof dateString === 'number') {
      // Unix timestamp
      date = new Date(dateString)
    } else {
      // Already a Date object
      date = dateString
    }

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      console.error('Invalid date:', dateString)
      return { date: 'Неверная дата', time: '' }
    }

    return {
      date: date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
      time: date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }),
    }
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return { date: 'Ошибка формата', time: '' }
  }
}

/**
 * Format date only (without time)
 */
export function formatDate(dateString: string | number | Date) {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверная дата'
    }

    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  } catch (error) {
    console.error('Error formatting date:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Format time only (without date)
 */
export function formatTime(dateString: string | number | Date, includeSeconds: boolean = false) {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверное время'
    }

    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    }

    if (includeSeconds) {
      options.second = '2-digit'
    }

    return date.toLocaleTimeString('ru-RU', options)
  } catch (error) {
    console.error('Error formatting time:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Format relative time (e.g., "2 часа назад")
 */
export function formatRelativeTime(dateString: string | number | Date): string {
  try {
    if (!dateString) return 'Не указано'

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Неверная дата'
    }

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffSec = Math.floor(diffMs / 1000)
    const diffMin = Math.floor(diffSec / 60)
    const diffHour = Math.floor(diffMin / 60)
    const diffDay = Math.floor(diffHour / 24)

    if (diffSec < 60) return 'только что'
    if (diffMin < 60) return `${diffMin} мин. назад`
    if (diffHour < 24) return `${diffHour} ч. назад`
    if (diffDay < 7) return `${diffDay} дн. назад`

    return formatDate(date)
  } catch (error) {
    console.error('Error formatting relative time:', dateString, error)
    return 'Ошибка формата'
  }
}

/**
 * Format date range
 */
export function formatDateRange(
  startDate: string | number | Date,
  endDate: string | number | Date
): string {
  const start = formatDate(startDate)
  const end = formatDate(endDate)
  return `${start} — ${end}`
}

/**
 * Parse date string to ISO format for API
 */
export function toISOString(dateString: string | number | Date): string {
  try {
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      throw new Error('Invalid date')
    }
    return date.toISOString()
  } catch (error) {
    console.error('Error converting to ISO string:', dateString, error)
    throw error
  }
}
